import { makeAutoObservable } from "mobx";
import Storage from "utils/storage";
import { get_symbol, get_plate } from "api/v4/market";
import { get_marketConfig } from "api/old/xt-lever";
import { get_marketTips } from "api/old/xt-app";
import { post_getEtfByTradeMarket } from "api/old/xt-redemption";
import { get_fapiSymbolList } from "api/old/futures";
import user from "./user";

export interface SymbolProps extends ObjAny {
  symbol: string;
  quantityPrecision: number;
  pricePrecision: number;
  plates: number[];
  state: string;
  nextState: null | string;
  nextStateTime: null | number;
  displayLevel: string;
  tradingEnabled: boolean;
  displayWeight: number;
  depthMergePrecision: number;
  /** 是否开启 AI 分析（GET /public/symbol） */
  aiAnalysisEnabled?: boolean;
}

export interface LeverSymbolProps extends ObjAny {
  marketName: string;
  maxLeverage: number;
  liquidationRate: string;
  tipsRate: string;
  dailyInterestRate: string;
}

export interface FuturesSymbolProps extends ObjAny {
  symbol: string;
}

export interface EtfConfigDataProps {
  id: number;
  multiple: number;
  dealPair: string;
}

interface EtfConfigProps {
  data: EtfConfigDataProps[];
}

interface TipsProps extends ObjAny {
  tipsType: number;
  content: string;
  link?: string;
}

interface PlateProps {
  id: string;
  plate: string;
}

export enum TypeEnum {
  spot = "SPOT",
  lever = "LEVER",
}

export interface IEtfDealPairItemProps {
  coinVol: number;
  dealPairName: string;
  name: string;
  high: number;
  low: number;
  moneyVol: number;
  multiple: number;
  price: number;
  rate: number;
}

interface StateProps {
  name: string;
  type: TypeEnum;
  tips: ObjT<TipsProps>;
  symbols: WithUndefined<SymbolProps[]>;
  leverConfigAry: WithUndefined<LeverSymbolProps[]>;
  etfConfig: WithUndefined<ObjT<EtfConfigProps>>;
  plateList: PlateProps[];
  netWorth: string;
  etfDealPairListData: IEtfDealPairItemProps[];
  isFuturesUsdtOpen: WithUndefined<boolean>;
  isFuturesCoinOpen: WithUndefined<boolean>;
  futuresUsdtTransferList: WithUndefined<string[]>;
  futuresCoinTransferList: WithUndefined<string[]>;
  futuresUsdtConfigAry: WithUndefined<FuturesSymbolProps[]>;
}

let loadingGetMarketConfig = false;
let loadingGetCfdMarketConfig = false;

const market = makeAutoObservable(
  {
    name: "" as StateProps["name"],
    type: TypeEnum.spot as StateProps["type"],
    tips: {} as ObjT<TipsProps>,
    symbols: undefined as StateProps["symbols"],
    leverConfigAry: undefined as StateProps["leverConfigAry"],
    etfConfig: undefined as StateProps["etfConfig"],
    plateList: [] as StateProps["plateList"],
    netWorth: "" as StateProps["netWorth"],
    etfDealPairListData: [] as StateProps["etfDealPairListData"],
    isFuturesUsdtOpen: undefined as StateProps["isFuturesUsdtOpen"],
    isFuturesCoinOpen: undefined as StateProps["isFuturesCoinOpen"],
    futuresUsdtTransferList: undefined as StateProps["futuresUsdtTransferList"],
    futuresCoinTransferList: undefined as StateProps["futuresCoinTransferList"],
    futuresUsdtConfigAry: undefined as StateProps["futuresUsdtConfigAry"],

    get config(): WithUndefined<ObjT<SymbolProps>> {
      if (!this.symbols) return;
      const doc: ObjT<SymbolProps> = {};
      this.symbols.map((obj: any) => (doc[obj.symbol] = obj));
      return doc;
    },

    get currentConfig(): Partial<SymbolProps> {
      if (!this.name || !this.config) return {};
      return this.config[this.name] || {};
    },

    get leverConfigObj(): WithUndefined<ObjT<LeverSymbolProps>> {
      if (!user.authInfo || !user.authInfo.country || user.authInfo.country.code == 971) return;
      if (!this.leverConfigAry) return;
      const doc: ObjT<LeverSymbolProps> = {};
      this.leverConfigAry.map((obj: any) => (doc[obj.marketName] = obj));
      return doc;
    },

    get currentLeverConfig(): Partial<LeverSymbolProps> {
      if (!this.leverConfigObj || !this.leverConfigObj[this.name]) return {};
      return this.leverConfigObj[this.name];
    },

    get isLever(): boolean {
      return this.type === TypeEnum.lever;
    },

    get isEtf(): boolean {
      return false;
    },

    updateState(payload: Partial<StateProps>) {
      for (const key in payload) {
        this[key] = payload[key];
      }
    },

    updateStateOnce(payload: Partial<StateProps>) {
      for (const key in payload) {
        if (!this[key]) this[key] = payload[key];
      }
    },

    formatName(symbol: string): string {
      return symbol.split("_").join("/").toUpperCase();
    },

    formatCfdName(symbol: string): string {
      if (!symbol) return "";
      return this.formatName(symbol.replace(/_cfd$/i, ""));
    },

    getMarketConfig() {
      if (loadingGetMarketConfig) return;
      loadingGetMarketConfig = true;

      const { version, symbols } = Storage.get("symbols") || {};
      this.symbols = symbols;

      get_symbol({
        params: {
          version,
        },
      })
        .then((data) => {
          if (data.symbols && data.version !== version) {
            this.symbols = data.symbols;
            Storage.set("symbols", data);
          }
        })
        .catch(() => {})
        .finally(() => {
          loadingGetMarketConfig = false;
        });
    },

    getCfdMarketConfig() {
      if (loadingGetCfdMarketConfig) return;
      loadingGetCfdMarketConfig = true;

      const { version, symbols } = Storage.get("cfdSymbols") || {};
      this.symbols = symbols;

      get_symbol({
        params: {
          version,
          types: "cfd",
        },
      })
        .then((data) => {
          if (data.symbols && data.version !== version) {
            this.symbols = data.symbols;
            Storage.set("cfdSymbols", data);
          }
        })
        .catch(() => {})
        .finally(() => {
          loadingGetCfdMarketConfig = false;
        });
    },

    getLeverMarketConfig(finallyFun?: () => void) {
      get_marketConfig()
        .then((data: StateProps["leverConfigAry"]) => {
          this.leverConfigAry = data;
        })
        .finally(finallyFun);
    },

    getFapiSymbolList() {
      get_fapiSymbolList().then((data) => {
        this.futuresUsdtConfigAry = data;
      });
    },

    getEtfByTradeMarket() {
      post_getEtfByTradeMarket({ data: { pairs: [] } }).then((data) => {
        const etfConfig = {};
        data.map(({ marketName, data }) => {
          etfConfig[marketName] = { data };
        });
        this.etfConfig = etfConfig;
      });
    },

    getMarketTips() {
      get_marketTips().then((data) => {
        const obj = {};
        data.map((doc) => {
          obj[doc.marketName] = doc;
        });
        this.tips = obj;
      });
    },

    getPlateList() {
      get_plate().then((data) => {
        this.plateList = data;
      });
    },

    isEtfSymbolFn(symbolCfg: SymbolProps) {
      if (symbolCfg.plates && symbolCfg.plates.includes(145)) return true;
      return false;
    },

    isMarketOpenFn(symbolCfg: ObjAny | SymbolProps, { server, local }): boolean {
      const { state, nextState, nextStateTime } = symbolCfg;
      if (state === "ONLINE") return true;
      if (state === "OFFLINE" && nextState === "ONLINE") {
        if (!nextStateTime || !server) return true;
        return server + (Date.now() - local) - nextStateTime > 0;
      }
      return true;
    },
  },
  {},
  {
    autoBind: true,
    deep: false,
  }
);

export default market;
